import React, { useEffect, useState } from "react";
// import { useLocation } from "react-router-dom/cjs/react-router-dom";
import {
  useLocation,
  useHistory,
} from "react-router-dom/cjs/react-router-dom.min";

import { Box, Grid, Link, Tab, Tabs, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  BooleanInput,
  CheckboxGroupInput,
  FileInput,
  FormDataConsumer,
  RadioButtonGroupInput,
  regex,
  required,
  SelectInput,
  TextInput,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { theme } from "../../../themes";
import { AttachFileIcon } from "../../../components/common/iconography";
import CustomFieldsInput from "../../../components/common/CustomFieldsInput";
import { FileField } from "../../../components/common/FileField";
import {
  CarlotLotNumberTokens,
  CustomFieldsDisplay,
  FifoLotNumberTokens,
  InboundSubjectTokens,
  LotTypes,
  MAX_PHOTO_NUM_INBOUND,
  MAX_PHOTO_NUM_INVENTORY,
  MAX_PHOTO_NUM_OUTBOUND,
  OutboundAdviceInputOptions,
  OutboundSubjectTokens,
  REGEX_MULTI_EMAIL,
  TrueOrFalse,
  UnitWeightOptions,
  Workflow,
} from "../../../utils/constants";
import { handleFileChange } from "../../../utils/fileUtils";
import { unitOptionRenderer } from "../../../utils/loadTypeUtils";
import { getLastValueFromUrl } from "../../../utils/utils";
import { BetaBadge, NewBadge } from "../../../components/common/FeatureBadges";
import { SmartTipIcon } from "../../../components/common/SmartTipIcon";
import { fetchEventTypes } from "../eventTypes/EventTypeUtils";
import { ChecklistPanel } from "./ChecklistPanel";
import { AddressBook } from "./AddressBook";
import { TokenInput } from "../../../components/common/TokenInput";

const useStyles = makeStyles((theme) => ({
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    minWidth: "16em",
    "& span:nth-child(1)": {
      flexDirection: "row",
      justifyContent: "flex-end",
      marginRight: theme.spacing(2),
    },
  },
  tabsContainer: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    width: "100%",
    marginTop: theme.spacing(3),
  },
  hiddenTab: {
    display: "none",
    width: "0%",
  },
  visibleTab: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginLeft: theme.spacing(3),
  },
  arrayInput: {
    marginTop: theme.spacing(-2),
  },
  booleanInput: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  checkbodInput: {
    marginTop: theme.spacing(-1),
    marginLeft: theme.spacing(1),
  },
}));

const validateRequired = [required()];
const validateMultiEmail = [regex(REGEX_MULTI_EMAIL, "Must be valid email(s)")];
const initFieldOptions = [
  { id: "lotNumber", name: "Lot Number" },
  { id: "poNumber", name: "PO Number" },
];
const inboundPhotoOptions = Array.from(
  { length: MAX_PHOTO_NUM_INBOUND + 1 },
  (v, k) => k,
).map((num) => ({
  id: num,
  name: num.toString(),
}));
const outboundPhotoOptions = Array.from(
  { length: MAX_PHOTO_NUM_OUTBOUND + 1 },
  (v, k) => k,
).map((num) => ({
  id: num,
  name: num.toString(),
}));
const inventoryPhotoOptions = Array.from(
  { length: MAX_PHOTO_NUM_INVENTORY + 1 },
  (v, k) => k,
).map((num) => ({
  id: num,
  name: num.toString(),
}));

// Required validator for TokenInput fields (stored as JSON string or array)
const validateTokenRequired = (value) => {
  debugger;
  if (value == null) return "ra.validation.required";
  try {
    const arr = Array.isArray(value) ? value : JSON.parse(value);
    if (!arr || arr.length === 0) return "ra.validation.required";
  } catch {
    return "ra.validation.required";
  }
  return undefined;
};

// Validation: {@lastN} must be immediately followed by a variable token;
// no two {@lastN} tokens can be consecutive; and {@lastN} cannot precede a computed "@..." token.
const validateCarlotTemplate = (value) => {
  if (!value) return undefined;
  let elements = value;
  if (!Array.isArray(elements)) {
    try {
      elements = JSON.parse(value);
    } catch {
      return "Invalid template format.";
    }
  }
  const isToken = (el) => el && el.type === "token";
  const isLastModifier = (el) =>
    isToken(el) && /^\{\s*@last\d+\s*\}$/i.test(el.value || "");
  const isComputedToken = (el) =>
    isToken(el) && /^\{\s*@/i.test(el.value || ""); // e.g., {@DD}, {@MM}, etc.

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (isLastModifier(el)) {
      const next = elements[i + 1];
      if (!isToken(next)) {
        return `"Last of" must be followed by a variable token.`;
      }
      if (isLastModifier(next)) {
        return `Two "Last of" tokens cannot be consecutive.`;
      }
      if (isComputedToken(next)) {
        return `"Last of" cannot be followed by a computed token.`;
      }
    }
  }
  return undefined;
};

export const AccountSettingsTab = ({ ...props }) => {
  const [fieldOptions, setFieldOptions] = useState(initFieldOptions);
  const [tabValue, setTabValue] = useState(0);
  const [eventTypes, setEventTypes] = useState(
    window.appUser.reloadYard.eventTypes?.items,
  );
  const { record } = props;
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (getLastValueFromUrl(history.location.pathname) === `customSettings`) {
      const searchParams = new URLSearchParams(
        location.pathname.includes("?")
          ? window.location.search
          : location.search,
      );
      const tabIndex = searchParams.get("t");
      if (tabIndex) {
        setTabValue(parseInt(tabIndex));
      }
    } else {
      setTabValue(0);
      if (location.search) {
        history.push(location.pathname);
      }
    }
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    if (record.inventoryCustomFields?.length > 0) {
      setFieldOptions([...initFieldOptions, ...record.inventoryCustomFields]);
    }
  }, [record?.inventoryCustomFields]);

  useEffect(() => {
    // Only outbound-capable event types may back the outbound advice default,
    // so the picker can't create a workflow mismatch (workflow-less types count
    // as "any" and are included).
    fetchEventTypes(setEventTypes, notify, Workflow.OUTBOUND);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    history.push({
      pathname: history.location.pathname,
      search: `?t=${newValue}`,
      state: history.location.state,
    });
  };

  const handlePickupNumber = (value, formData) => {
    debugger;
    if (value && formData.outboundAdviceInputs?.includes("pickupNumber")) {
      // if auto-gen pickup number - do not allow pickupNumber input
      form.change(
        "outboundAdviceInputs",
        formData.outboundAdviceInputs.filter((item) => item !== "pickupNumber"),
      );
    }
  };

  const handleOutboundAdviceChange = (value) => {
    debugger;
    if (
      value.includes("pickupNumber") ||
      value.includes("carrier") ||
      value.includes("railCarNumber") ||
      value.includes("mustTarp")
    ) {
      if (!value.includes("carrierType")) {
        form.change("outboundAdviceInputs", [...value, "carrierType"]);
      }
    }
    if (value.includes("pickupNumber")) {
      // if pickupNumber input - disable auto-gen pickup number
      form.change("generatePickupNumber", false);
    }
  };

  const getOutboundAdviceInputOptions = () => {
    if (!eventTypes?.length) {
      return OutboundAdviceInputOptions.filter(
        (itm) => itm.id !== "scheduledDateTime",
      );
    }
    return OutboundAdviceInputOptions;
  };

  const renderRailcarVerificationLabel = (
    label,
    productlyId,
    smartTipIconLabel,
  ) => (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
      <span>{label}</span>
      <Box
        component="span"
        id={productlyId}
        data-productly={productlyId}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "pointer",
          ml: 0.5,
        }}
      >
        <SmartTipIcon
          label={smartTipIconLabel}
          alignRight={false}
          alignMiddle
          mt={6.2}
          ml={-1.32}
        />
      </Box>
    </Box>
  );

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <div className={classes.tabsContainer}>
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Settings Tabs"
            className={classes.tabs}
          >
            <Tab id="inboundFields" label="Inbound" />
            <Tab id="outboundFields" label="Outbound" />
            <Tab id="inventoryFields" label="Inventory" />
            <Tab id="addressBook" label="Address Book" />
            <Tab id="docTemplates" label="Doc Templates" />
            <Tab
              id="checklists"
              label={
                <>
                  <BetaBadge /> Checklists
                </>
              }
            />
          </Tabs>
          <TabPanel value={tabValue} index={0} classes={classes}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      Proof Of Delivery Email
                    </Typography>
                  </legend>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <TextInput
                        source="podEmail"
                        label="POD Email"
                        variant="outlined"
                        fullWidth
                        helperText="Comma-separated email(s)"
                        validate={validateMultiEmail}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <TokenInput
                        source="podSubject"
                        label="POD Subject"
                        validate={[validateTokenRequired]}
                        availableTokens={InboundSubjectTokens}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <BooleanInput
                        source="podPhotos"
                        label="Enclose Photos"
                        fullWidth
                        className={classes.booleanInput}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <BooleanInput
                        source="podAttachments"
                        label="Enclose Attachments"
                        fullWidth
                        className={classes.booleanInput}
                      />
                    </Grid>
                  </Grid>
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      Inbound Workflow
                    </Typography>
                  </legend>
                  <Grid container spacing={1}>
                    <Grid item xs={3}>
                      <SelectInput
                        source="inboundPhotos"
                        label="Number of Photos (Mim.)"
                        variant="outlined"
                        fullWidth
                        choices={inboundPhotoOptions}
                        defaultValue={0}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <SelectInput
                        source="lotType"
                        label="Default Lot Type"
                        choices={LotTypes}
                        allowEmpty
                        variant="outlined"
                        fullWidth
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <RadioButtonGroupInput
                          source="lotNumberOverride"
                          label="Override Lot Number"
                          row
                          choices={TrueOrFalse}
                          defaultValue={false}
                          style={{
                            marginTop: 0,
                            marginLeft: theme.spacing(1),
                          }}
                          helperText={false}
                        />
                        <NewBadge mt={-5} label="Override Lot Number" />
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <TokenInput
                        source="fifoLotNumber"
                        label="FIFO Lot Number"
                        validate={[validateTokenRequired]}
                        prefix={formData.lotNumberPrefix}
                        availableTokens={FifoLotNumberTokens}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TokenInput
                        source="carlotLotNumber"
                        label="Spec Id Lot Number"
                        validate={[
                          validateTokenRequired,
                          validateCarlotTemplate,
                        ]}
                        prefix={formData.lotNumberPrefix}
                        availableTokens={CarlotLotNumberTokens}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      <RadioButtonGroupInput
                        source="requireDefaultZone"
                        label="Require Default Zone"
                        row
                        choices={TrueOrFalse}
                        defaultValue={true}
                        style={{
                          marginTop: 0,
                          marginLeft: theme.spacing(1),
                        }}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <RadioButtonGroupInput
                        source="secondaryReview"
                        label="Secondary Review"
                        row
                        choices={TrueOrFalse}
                        defaultValue={false}
                        style={{
                          marginTop: 0,
                          marginLeft: theme.spacing(1),
                        }}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <RadioButtonGroupInput
                        source="verifyOutboundRailCar"
                        label={renderRailcarVerificationLabel(
                          "Verify Arriving / Receiving Railcar",
                          "verify-arriving-receiving-railcar",
                          "verify-arriving-receiving-railcar-icon",
                        )}
                        row
                        choices={TrueOrFalse}
                        defaultValue={false}
                        style={{
                          marginTop: 0,
                          marginLeft: theme.spacing(1),
                        }}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <RadioButtonGroupInput
                          source="trackInboundSeals"
                          label="Track Inbound Seals"
                          row
                          choices={TrueOrFalse}
                          defaultValue={false}
                          style={{
                            marginTop: 0,
                            marginLeft: theme.spacing(1),
                          }}
                          helperText={false}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      CUSTOM FIELDS
                    </Typography>
                  </legend>
                  <CustomFieldsInput
                    {...props}
                    source="inboundCustomFields"
                    displayValues={[
                      CustomFieldsDisplay.MOBILE,
                      CustomFieldsDisplay.POD,
                    ]}
                    className={classes.arrayInput}
                  />
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  source="inboundInstructions"
                  label="Inbound Instructions"
                  variant="outlined"
                  multiline
                  minRows="5"
                  fullWidth
                  helperText={false}
                />
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={1} classes={classes}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      Proof Of Shipment Email
                    </Typography>
                  </legend>
                  <Grid container>
                    <Grid item xs={4}>
                      <TextInput
                        source="posEmail"
                        label="POS Email"
                        variant="outlined"
                        fullWidth
                        helperText="Comma-separated email(s)"
                        validate={validateMultiEmail}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <TokenInput
                        source="posSubject"
                        label="POS Subject"
                        validate={[validateTokenRequired]}
                        availableTokens={OutboundSubjectTokens}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <BooleanInput
                        source="posPhotos"
                        label="Enclose Photos"
                        fullWidth
                        className={classes.booleanInput}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <BooleanInput
                        source="posAttachments"
                        label="Enclose Attachments & BOL"
                        fullWidth
                        className={classes.booleanInput}
                      />
                    </Grid>
                  </Grid>
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      OUTBOUND ADVICE SETUP
                    </Typography>
                    {/* <BetaBadge label="OUTBOUND ADVICE SETUP" /> */}
                  </legend>
                  <Grid container spacing={0}>
                    <Grid item xs={12}>
                      <CheckboxGroupInput
                        source="outboundAdviceInputs"
                        label="Advice Inputs"
                        row={true}
                        fullWidth
                        choices={getOutboundAdviceInputOptions()}
                        onChange={(value) => handleOutboundAdviceChange(value)}
                        className={classes.checkbodInput}
                        helperText={false}
                      />
                    </Grid>
                    {formData.outboundAdviceInputs?.includes(
                      "scheduledDateTime",
                    ) && (
                      <Grid item xs={12} md={4}>
                        <SelectInput
                          source="outboundEventTypeId"
                          label="Default Event Type"
                          variant="outlined"
                          fullWidth
                          choices={eventTypes}
                          defaultValue={
                            eventTypes?.length === 1 ? eventTypes[0].id : ""
                          }
                          validate={validateRequired}
                          helperText={false}
                        />
                      </Grid>
                    )}
                  </Grid>
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      OUTBOUND WORKFLOW
                    </Typography>
                  </legend>
                  <Grid container>
                    <Grid item xs={4}>
                      <SelectInput
                        source="outboundPhotos"
                        label="Number of Photos (Mim.)"
                        variant="outlined"
                        fullWidth
                        choices={outboundPhotoOptions}
                        defaultValue={0}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <RadioButtonGroupInput
                        source="mustTarp"
                        label="Must Tarp?"
                        row
                        choices={TrueOrFalse}
                        defaultValue={false}
                        fullWidth
                        style={{
                          marginTop: 0,
                          marginLeft: theme.spacing(1),
                        }}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <RadioButtonGroupInput
                        source="outboundSkipSignature"
                        label="Skip BOL e-signatures"
                        row
                        choices={TrueOrFalse}
                        defaultValue={false}
                        style={{
                          marginTop: 0,
                          marginLeft: theme.spacing(1),
                        }}
                        helperText={false}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={4}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <RadioButtonGroupInput
                          source="generatePickupNumber"
                          label="Auto-generate Pickup Number"
                          row
                          choices={TrueOrFalse}
                          defaultValue={false}
                          onChange={(value) =>
                            handlePickupNumber(value, formData)
                          }
                          style={{
                            marginTop: 0,
                            marginLeft: theme.spacing(1),
                          }}
                          // className={classes.booleanInput}
                          helperText={false}
                        />
                        <BetaBadge
                          ml={-5}
                          mt={-5}
                          label="Auto-generate Pickup Number"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <RadioButtonGroupInput
                        source="verifyInboundRailCar"
                        label={renderRailcarVerificationLabel(
                          "Verify Supplying Railcar",
                          "verify-supplying-railcar",
                          "verify-supplying-railcar-icon",
                        )}
                        row
                        choices={TrueOrFalse}
                        defaultValue={false}
                        style={{
                          marginTop: 0,
                          marginLeft: theme.spacing(1),
                        }}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <RadioButtonGroupInput
                          source="trackOutboundSeals"
                          label="Track Outbound Seals"
                          row
                          choices={TrueOrFalse}
                          defaultValue={false}
                          style={{
                            marginTop: 0,
                            marginLeft: theme.spacing(1),
                          }}
                          helperText={false}
                        />
                      </Box>
                    </Grid>
                    {/* <Grid item xs={4} >
                          <BooleanInput source="outboundReview" label="Outbound Review" fullWidth
                              className={classes.booleanInput}
                              helperText={false}
                          />
                      </Grid> */}
                  </Grid>
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      CUSTOM FIELDS
                    </Typography>
                  </legend>
                  <CustomFieldsInput
                    {...props}
                    source="outboundCustomFields"
                    displayValues={[
                      CustomFieldsDisplay.MOBILE,
                      CustomFieldsDisplay.POD,
                    ]}
                    className={classes.arrayInput}
                  />
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  source="outboundInstructions"
                  label="Outbound Instructions"
                  variant="outlined"
                  multiline
                  minRows="5"
                  fullWidth
                  helperText={false}
                />
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={2} classes={classes}>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <SelectInput
                  source="defaultWeightUOM"
                  label="Default Weight UOM"
                  variant="outlined"
                  fullWidth
                  choices={UnitWeightOptions}
                  optionText={unitOptionRenderer}
                  allowEmpty
                />
              </Grid>
              <Grid item xs={4}>
                <SelectInput
                  source="inventoryPrimaryField"
                  label="Primary Display Field"
                  variant="outlined"
                  fullWidth
                  choices={fieldOptions}
                />
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      Inventory Workflow
                    </Typography>
                  </legend>
                  <Grid container>
                    <Grid item xs={4}>
                      <SelectInput
                        source="inventoryPhotos"
                        label="Number of Photos (Mim.)"
                        variant="outlined"
                        fullWidth
                        choices={inventoryPhotoOptions}
                        defaultValue={0}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <RadioButtonGroupInput
                          source="trackInventorySeals"
                          label="Track Inventory Seals"
                          row
                          choices={TrueOrFalse}
                          defaultValue={false}
                          style={{
                            marginTop: 0,
                            marginLeft: theme.spacing(1),
                          }}
                          helperText={false}
                        />
                        <BetaBadge mt={-5} label="Track Inventory Seals" />
                      </Box>
                    </Grid>
                    {/* <Grid item xs={4} >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <BooleanInput source="isBarcodeRequired" label="Scan Unit Barcode" fullWidth
                              className={classes.booleanInput}
                              helperText={false}
                          />
                          <BetaBadge ml={-1} label="Scan Unit Seals" />
                      </Box>
                  </Grid> */}
                  </Grid>
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      CUSTOM FIELDS
                    </Typography>
                  </legend>
                  <CustomFieldsInput
                    {...props}
                    source="inventoryCustomFields"
                    displayValues={[
                      CustomFieldsDisplay.MOBILE,
                      CustomFieldsDisplay.TALLYSHEET,
                      CustomFieldsDisplay.POD,
                      CustomFieldsDisplay.POS,
                    ]}
                    className={classes.arrayInput}
                  />
                </fieldset>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={3} classes={classes}>
            <AddressBook {...props} />
          </TabPanel>
          <TabPanel value={tabValue} index={4} classes={classes}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography
                  variant="overline"
                  color="textSecondary"
                  gutterBottom
                >
                  Instructions
                </Typography>
                <ol>
                  <li>
                    {" "}
                    Download{" "}
                    <Link
                      href="https://transloads.co/docs/customizing-docx-document-templates"
                      target="_blank"
                      rel="noreferrer"
                      underline="hover"
                    >
                      document template
                    </Link>
                    .
                  </li>
                  <li>
                    {" "}
                    Customize following the instructinos and excamples on the
                    download page{" "}
                  </li>
                  <li> Save as a .docx file and upload here </li>
                  <li>
                    {" "}
                    If errors are found, review, correct the errors and
                    re-upload{" "}
                  </li>
                  <li>
                    {" "}
                    For help, contact us at 877-TRANSLOAD / 877-872-6756{" "}
                  </li>
                </ol>
              </Grid>
              <Grid item xs={12} md={6}>
                <FileInput
                  source="receiptTemplate"
                  label="INBOUND RECEIPT TEMPLATE"
                  variant="outlined"
                  fullWidth
                  placeholder={
                    <p>
                      <AttachFileIcon fontSize="large" /> <br /> Drop a DOCX
                      file to upload, or click to select it.
                    </p>
                  }
                  accept=".docx"
                  onChange={(value) =>
                    handleFileChange("receiptTemplate", value, form, notify)
                  }
                >
                  <FileField source="src" title="title" target="_blank" />
                </FileInput>
              </Grid>
              <Grid item xs={12} md={6}>
                <FileInput
                  source="bolTemplate"
                  label="BILL OF LADING TEMPLATE"
                  variant="outlined"
                  fullWidth
                  placeholder={
                    <p>
                      <AttachFileIcon fontSize="large" /> <br /> Drop a DOCX
                      file to upload, or click to select it.
                    </p>
                  }
                  accept=".docx"
                  onChange={(value) =>
                    handleFileChange("bolTemplate", value, form, notify)
                  }
                >
                  <FileField source="src" title="title" target="_blank" />
                </FileInput>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={5} classes={classes}>
            <ChecklistPanel {...props} />
          </TabPanel>
        </div>
      )}
    </FormDataConsumer>
  );
};

const TabPanel = (props) => {
  const { classes, children, value, index, ...other } = props;

  return (
    <div
      className={value === index ? classes.visibleTab : classes.hiddenTab}
      role="tabpanel"
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      hidden={value !== index}
      {...other}
    >
      {children}
    </div>
  );
};
